<template>
  <div v-if="isAllowedByGroups" :class="$style.bannerStatus">
    <MainBannerSlider
      :class="$style.actionBannerMain"
      :banners="pageBanners"
      :disable-button="!checkTournamentStatus() && !isPlayerAuthorised"
      @buttonClick="scrollToAction"
    >
      <template #beforeButtom>
        <template v-if="hasActiveFinaledRound && isUserInWinGroup">
          <ActionParticipate
            :is-action-participate-loser="true"
            :icon="storeComponents.infoDropdownAction.iconActionHeader.url"
            :text="storeComponents.infoDropdownAction.successfullyParticipateAction"
          />
        </template>
        <template v-else-if="checkTournamentStatus()">
          <ActionParticipate
            v-if="isUserInWinGroup"
            :class="$style.actionParticipateBanner"
            :is-action-participate-successfull="true"
            :icon="storeComponents.infoDropdownAction.iconActionHeader.url"
            :text="storeComponents.infoDropdownAction.qualificationWinnerToFinalRound"
          />
          <ActionParticipate
            v-if="isUserNotInWinGroup"
            :class="$style.actionParticipateBanner"
            :icon="storeComponents.infoDropdownAction.iconActionHeader.url"
            :text="storeComponents.infoDropdownAction.qualificationLosersIsSuccessfully"
          />
        </template>
      </template>
    </MainBannerSlider>
  </div>
</template>

<script lang="ts" setup>
import ActionParticipate from '~/components/action/actionParticipateText.vue';
import { MainBannerSlider } from '~/components/lib/mainPage';
import { useStoreSsPlayer } from '~/stores/softswiss/player';
import { useStoreSsTournaments } from '~/stores/softswiss/tournaments';
import { useStoreButtonTitles } from '~/stores/strapi/buttonTitles';
import { useStoreComponents } from '~/stores/strapi/components';
import { ERaceStatus, ERaceType } from '~/types/races';
import { formatDate } from '~/utils/date/helpers';
import { findIfPassesByGroups } from '~/utils/strapiComponents/filters';
import type { ComponentInfoPageBlocksActionsBannerBlock } from '~/types/infoPages';

const props = defineProps({
  data: {
    type: Object as PropType<ComponentInfoPageBlocksActionsBannerBlock>,
    default: () => ({}),
  },
});

const storeSsPlayer = useStoreSsPlayer();
const storeComponents = useStoreComponents();
const storeButtons = useStoreButtonTitles();

const storeSsTournaments = useStoreSsTournaments();

const isAllowedByGroups = computed(() => {
  if (!storeSsPlayer.isPlayerAuthorised && props.data.groupFilter?.hideForGuests) {
    return false;
  }

  const isAllowedByGroups = findIfPassesByGroups(
    props.data.groupFilter?.includedGroups,
    props.data.groupFilter?.excludedGroups
  );

  if (!isAllowedByGroups) {
    return false;
  }

  return true;
});

const formattedStartDate = computed(() =>
  props.data.startDate ? formatDate(new Date(props.data.startDate)) : ''
);

const formattedEndDate = computed(() =>
  props.data.endDate ? formatDate(new Date(props.data.endDate)) : ''
);

// Is the user in one of the groups a loser or not
const tournamentsListToShow = computed(() => {
  return storeSsTournaments.tournamentsExtended.filter(
    (tournament) => tournament.raceData.raceType === ERaceType.SpecialEvent
  );
});

const progressBars = computed(() => {
  return tournamentsListToShow.value.flatMap((tournament) => tournament.raceData.progressBar);
});

const isUserInWinGroup = computed(() => {
  return progressBars.value.some(
    (progressData) =>
      progressData.stageNumer !== 1 && storeSsPlayer.playerGroupIds.includes(progressData.groupId)
  );
});

const isUserNotInWinGroup = computed(() => {
  if (isUserInWinGroup.value) {
    return false;
  }
  return progressBars.value.some(
    (progressData) =>
      progressData.stageNumer !== 2 && storeSsPlayer.playerGroupIds.includes(progressData.groupId)
  );
});
// Is the user in one of the groups a loser or not

const hasActiveFinaledRound = computed(() => {
  return tournamentsListToShow.value.some(
    (tournament) =>
      tournament.raceData.actionFinaledRound === true &&
      tournament.tournamentData.status === ERaceStatus.active
  );
});

const isPlayerAuthorised = computed(() => storeSsPlayer.isPlayerAuthorised);

const scrollToAction = () => {
  const element = document.querySelector('[data-anchor="scrollTo"]');
  if (element) {
    const htmlElement = element as HTMLElement;
    window.scrollTo({
      top: htmlElement.offsetTop - 40,
      behavior: 'smooth',
    });
  }
};

const pageBanners = computed(() => {
  return [
    {
      frontImage: props.data.banner,
      subtitleFirst: props.data.title,
      subtitleSecond: props.data.prize,
      finishDate: formattedEndDate.value,
      startDate: formattedStartDate.value,
      buttonText: storeButtons.join,
    },
  ];
});

const checkTournamentStatus = () => {
  return tournamentsListToShow.value.some((tournament) => {
    const matchingStatus = storeSsTournaments.tournamentsStatuses.find(
      (status) => status.frontId === tournament.raceData.ssId
    );
    return matchingStatus ? matchingStatus.status === true : false;
  });
};

onMounted(async () => {
  await storeSsTournaments.loadTournaments();
  await storeSsTournaments.loadPlayerStatusesInTournaments();
});
</script>

<style lang="scss" module src="./actionsBannerBlock.scss"></style>
